import { useEffect, useCallback } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { auth0Config } from './config/auth0';
import theme from './theme/theme';
import HomePage from './pages/HomePage';

const CenteredLayout = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  gap: 16,
});

const AuthenticatedApp = () => {
  const { isLoading, isAuthenticated, error, loginWithRedirect, getAccessTokenSilently } = useAuth0();

  const validateToken = useCallback(async () => {
    try {
      await getAccessTokenSilently();
    } catch {
      loginWithRedirect();
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  useEffect(() => {
    if (!isAuthenticated || isLoading) return;

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        validateToken();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [isAuthenticated, isLoading, validateToken]);

  useEffect(() => {
    if (error) {
      const isRecoverable = error.message === 'Invalid state' || error.message === 'Login required';
      if (isRecoverable) {
        localStorage.removeItem(`@@auth0spajs@@::${auth0Config.clientId}::@@user@@`);
        loginWithRedirect();
      }
    }
  }, [error, loginWithRedirect]);

  if (isLoading) {
    return (
      <CenteredLayout>
        <CircularProgress color="primary" />
        <Typography variant="body2" color="textSecondary">Loading...</Typography>
      </CenteredLayout>
    );
  }

  if (error) {
    const isRecoverable = error.message === 'Invalid state' || error.message === 'Login required';
    if (isRecoverable) {
      return (
        <CenteredLayout>
          <CircularProgress color="primary" />
          <Typography variant="body2" color="textSecondary">Redirecting to login...</Typography>
        </CenteredLayout>
      );
    }

    return (
      <CenteredLayout>
        <Typography variant="h6" color="error">Authentication Error</Typography>
        <Typography variant="body2" color="textSecondary">{error.message}</Typography>
        <Button variant="contained" color="primary" onClick={() => loginWithRedirect()}>
          Try Again
        </Button>
      </CenteredLayout>
    );
  }

  if (!isAuthenticated) {
    loginWithRedirect();
    return (
      <CenteredLayout>
        <CircularProgress color="primary" />
        <Typography variant="body2" color="textSecondary">Redirecting to login...</Typography>
      </CenteredLayout>
    );
  }

  return <HomePage />;
};

const App = () => (
  <Auth0Provider
    domain={auth0Config.domain}
    clientId={auth0Config.clientId}
    authorizationParams={auth0Config.authorizationParams}
    useRefreshTokens={true}
    cacheLocation="localstorage"
  >
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthenticatedApp />
    </ThemeProvider>
  </Auth0Provider>
);

export default App;
